import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown, ChevronRight,
  Users, FileText, AlertTriangle, RotateCcw, Pencil, CalendarPlus, UserPlus, UserMinus, Receipt, Send, Trash2,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import PatientUpcoming from '../components/PatientUpcoming';
import { t, formatMoney, ageAt } from '../i18n';
import {
  Card, Button, Input, Select, Field, Badge, Alert, Spinner, EmptyState, Modal, Table, Textarea,
} from '../components/ui';

/**
 * The professional's patient roster.
 *
 * Each row is one treatment: this professional and one patient (migration
 * 0040). The same person may be seen by other professionals too, and nothing
 * here says so — their sessions, prices and notes are theirs. What is shared is
 * the person: name, date of birth, contact.
 *
 * Coverage and the agreed value are editable here because they are agreed
 * between professional and patient — the patient cannot set either themselves.
 * Changing them re-prices this professional's future unpaid sessions only.
 */

const STATUS_TONE = { approved: 'success', pending: 'warning', declined: 'danger', revoked: 'neutral' };
// Amber for "we cannot invite them yet", because that one needs the
// professional to do something.
const ACCESS_TONE = { none: 'warning', own: 'warning', invited: 'info', active: 'success' };

const TREATMENT_SELECT = `
  id, status, approved_at, billing_mode, insurance_plan_id, fee_override, needs_invoice,
  consecutive_cancellations, discharged_at,
  insurance_plans ( id, name, insurance_companies ( id, name ) ),
  dependents ( id, first_name, last_name, date_of_birth, relationship_to_account,
    accounts ( first_name, last_name, email, phone, auth_user_id ) )
`;

/**
 * The person, with this professional's treatment laid over it. `id` stays the
 * patient record's, which is what sessions, notes and the email are keyed on.
 */
const toPatient = ({ dependents: person, id: treatmentId, ...treatment }) => ({
  ...person, ...treatment, treatment_id: treatmentId,
});

/** Being seen now, or asking to be. Everyone else is a former patient. */
const isCurrent = (patient) => patient.status === 'approved' || patient.status === 'pending';

/**
 * An adult filed under someone else's account. The holder no longer sees them
 * (migration 0038), so they need an account of their own — which setting their
 * email creates.
 */
const needsOwnAccount = (patient) => {
  if (!patient || patient.relationship_to_account === 'self' || !patient.date_of_birth) return false;
  const [year, month, day] = patient.date_of_birth.split('-').map(Number);
  return new Date() >= new Date(year + 18, month - 1, day);
};

const BILLING_MODES = ['plan', 'particular'];

/**
 * How this patient pays, as an explicit choice rather than inferred from
 * whether a coverage was picked: a particular patient may keep their obra
 * social on file without being charged through it. Reintegro went in 0049.
 */
const BillingModeField = ({ value, onChange }) => (
  <Field label={t('insurance.billingMode')} required>
    <div className="grid gap-2 sm:grid-cols-2">
      {BILLING_MODES.map((mode) => (
        <label
          key={mode}
          className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-sm transition ${
            value === mode ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2 font-medium text-slate-900">
            <input type="radio" checked={value === mode} onChange={() => onChange(mode)} />
            {t(`insurance.billingModes.${mode}`)}
          </span>
          <span className="text-xs text-slate-500">{t(`insurance.billingModeHints.${mode}`)}</span>
        </label>
      ))}
    </div>
  </Field>
);

const agreedFeeHint = (mode) => t(mode === 'plan' ? 'patients.agreedFeePlanHint' : 'patients.agreedFeeHint');

const EMPTY_REFERRAL = { therapistId: '', note: '', agreed: false };

const PatientsView = () => {
  const { therapistId, profile } = useSession();
  const [patients, setPatients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [copays, setCopays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ billingMode: 'particular', companyId: '', planId: '', fee: '', needsInvoice: false });
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showDischarged, setShowDischarged] = useState(false);
  const [referring, setReferring] = useState(null);
  const [referral, setReferral] = useState(EMPTY_REFERRAL);
  const [directory, setDirectory] = useState([]);
  const [newPatient, setNewPatient] = useState({
    firstName: '', lastName: '', dateOfBirth: '', email: '', phone: '',
    idNumber: '', companyId: '', planId: '', fee: '', billingMode: 'particular', needsInvoice: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    // This professional's own treatments, admin's included: another
    // professional's patients are that professional's alone (migration 0038).
    // A request they declined without ever seeing the person is not a patient.
    const [treatmentResult, companyResult, planResult, copayResult] = await Promise.all([
      supabase
        .from('treatments')
        .select(TREATMENT_SELECT)
        .eq('therapist_id', therapistId)
        .or('status.neq.declined,approved_at.not.is.null'),
      supabase.from('insurance_companies').select('id,name').eq('is_active', true).order('name'),
      supabase.from('insurance_plans').select('id,name,company_id').eq('is_active', true).order('name'),
      supabase.from('therapist_insurance_plans')
        .select('insurance_plan_id, patient_copay').eq('therapist_id', therapistId),
    ]);

    if (treatmentResult.error) setError(treatmentResult.error.message);
    setPatients((treatmentResult.data ?? [])
      .filter((row) => row.dependents)
      .map(toPatient)
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es')));
    setCompanies(companyResult.data ?? []);
    setPlans(planResult.data ?? []);
    setCopays(copayResult.data ?? []);
    setLoading(false);
  }, [therapistId]);

  useEffect(() => { load(); }, [load]);

  // Discharged patients stay in the data forever — the clinical history must
  // be kept — but they are off the working roster unless asked for.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return patients
      .filter((patient) => (showDischarged ? !isCurrent(patient) : isCurrent(patient)))
      .filter((patient) => !needle
        || `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(needle)
        || (patient.accounts?.email ?? '').toLowerCase().includes(needle));
  }, [patients, search, showDischarged]);

  const dischargedCount = useMemo(() => patients.filter((patient) => !isCurrent(patient)).length, [patients]);
  const currentCount = patients.length - dischargedCount;

  const openEdit = (patient) => {
    setEditing(patient);
    setDraft({
      billingMode: patient.billing_mode,
      companyId: patient.insurance_plans?.insurance_companies?.id ?? '',
      planId: patient.insurance_plan_id ?? '',
      fee: patient.fee_override != null ? String(patient.fee_override) : '',
      // The holder's address is not theirs to inherit.
      email: needsOwnAccount(patient) ? '' : (patient.accounts?.email ?? ''),
      needsInvoice: Boolean(patient.needs_invoice),
    });
  };

  const saveCoverage = async () => {
    setBusy(true);
    // Prestador prices off the obra social, so it needs one. Particular may
    // keep one on file for reference.
    if (draft.billingMode !== 'particular' && !draft.planId) {
      setError(t('patients.pickCompany'));
      setBusy(false);
      return;
    }
    const planId = draft.companyId ? (draft.planId || null) : null;
    const fee = draft.fee === '' ? null : Number(draft.fee);

    // The email lives on the account, which a professional may only read, so it
    // goes through its own function. Sent first: if the address is rejected,
    // the coverage change has not happened yet and the screen still shows what
    // they typed.
    // For an adult under someone else's account, an address is not an edit of
    // the holder's: it gives them an account of their own.
    const ownAccount = needsOwnAccount(editing);
    const claimed = Boolean(editing.accounts?.auth_user_id) && !ownAccount;
    const emailChanged = !claimed
      && draft.email.trim().toLowerCase() !== (ownAccount ? '' : (editing.accounts?.email ?? ''));
    if (emailChanged) {
      const { error: emailError } = await supabase.rpc('set_patient_email', {
        p_dependent_id: editing.id,
        p_email: draft.email.trim() || null,
      });
      if (emailError) { setError(emailError.message); setBusy(false); return; }
    }

    // Only the coverage change re-prices anything, so only it gets to say so.
    const coverageChanged = draft.billingMode !== editing.billing_mode
      || planId !== editing.insurance_plan_id
      || fee !== (editing.fee_override == null ? null : Number(editing.fee_override));
    const invoiceChanged = draft.needsInvoice !== Boolean(editing.needs_invoice);

    if (coverageChanged || invoiceChanged) {
      // An empty agreed value means "use the default for their billing mode",
      // not zero. Only this treatment changes: the same person's sessions with
      // another professional keep that professional's price.
      const { error: saveError } = await supabase.rpc('update_treatment_billing', {
        p_treatment_id: editing.treatment_id,
        p_billing_mode: draft.billingMode,
        p_insurance_plan_id: planId,
        p_fee_override: fee,
        p_needs_invoice: draft.needsInvoice,
      });
      if (saveError) { setError(saveError.message); setBusy(false); return; }
    }
    setBusy(false);
    setEditing(null);
    setNotice(coverageChanged
      ? t('patients.coverageSaved')
      : emailChanged && !invoiceChanged ? t('patients.emailSaved') : t('patients.detailsSaved'));
    load();
  };

  const createPatient = async () => {
    if (newPatient.billingMode !== 'particular' && !newPatient.planId) {
      setError(t('patients.pickCompany'));
      return;
    }
    setBusy(true);
    setError(null);
    const { error: createError } = await supabase.rpc('create_patient', {
      p_first_name: newPatient.firstName.trim(),
      p_last_name: newPatient.lastName.trim(),
      p_date_of_birth: newPatient.dateOfBirth || null,
      p_email: newPatient.email.trim() || null,
      p_phone: newPatient.phone.trim() || null,
      p_id_number: newPatient.idNumber.trim() || null,
      p_insurance_plan_id: newPatient.planId || null,
      p_fee_override: newPatient.fee === '' ? null : Number(newPatient.fee),
      p_relationship: 'self',
      p_billing_mode: newPatient.billingMode,
      p_needs_invoice: newPatient.needsInvoice,
    });
    setBusy(false);
    if (createError) { setError(createError.message); return; }
    setAdding(false);
    setNewPatient({ firstName: '', lastName: '', dateOfBirth: '', email: '', phone: '', idNumber: '', companyId: '', planId: '', fee: '', billingMode: 'particular', needsInvoice: false });
    setNotice(t('patients.created'));
    load();
  };

  const discharge = async (patient) => {
    const name = `${patient.first_name} ${patient.last_name}`;
    const reason = window.prompt(t('patients.dischargePrompt', { name }));
    if (reason === null) return;
    setBusy(true);
    const callDischarge = (cancelFuture) => supabase.rpc('discharge_patient', {
      p_dependent_id: patient.id, p_reason: reason || null, p_cancel_future: cancelFuture,
    });
    let { data, error: dErr } = await callDischarge(null);
    // Future sessions are reported first and nothing is discharged: left
    // booked, they kept sending reminders to someone no longer treated.
    if (!dErr && data && !data.discharged) {
      if (!window.confirm(t('patients.dischargeFutureConfirm', { name, count: data.future_sessions }))) {
        setBusy(false);
        return;
      }
      ({ data, error: dErr } = await callDischarge(true));
    }
    setBusy(false);
    if (dErr) { setError(dErr.message); return; }
    setNotice(data?.cancelled
      ? t('patients.dischargedWithCancellations', { name, count: data.cancelled })
      : t('patients.discharged', { name }));
    load();
  };

  const readmit = async (patient) => {
    const name = `${patient.first_name} ${patient.last_name}`;
    const { error: rErr } = await supabase.rpc('readmit_patient', { p_dependent_id: patient.id });
    if (rErr) { setError(rErr.message); return; }
    setNotice(t('patients.readmitted', { name }));
    load();
  };

  // Only a record this professional loaded and nothing has used (0062). The
  // database says whether it qualifies, and why not when the reason is theirs
  // to know; anything else is Dar de baja. The per-patient block that sat here
  // became Dar de baja too (0064).
  const deleteRecord = async (patient) => {
    const name = `${patient.first_name} ${patient.last_name}`;
    setError(null);
    const { data: verdict, error: checkError } = await supabase.rpc('delete_patient_record', {
      p_dependent_id: patient.id, p_check_only: true,
    });
    if (checkError) { setError(checkError.message); return; }
    if (!verdict?.deletable) { setError(verdict?.reason ?? t('patients.cannotDelete')); return; }
    if (!window.confirm(t('patients.confirmDelete', { name }))) return;
    const { error: deleteError } = await supabase.rpc('delete_patient_record', { p_dependent_id: patient.id });
    if (deleteError) { setError(deleteError.message); return; }
    setNotice(t('patients.deleted', { name }));
    load();
  };

  const openReferral = async (patient) => {
    setReferring(patient);
    setReferral(EMPTY_REFERRAL);
    const { data } = await supabase.rpc('professional_directory');
    setDirectory((data ?? []).filter((row) => row.id !== therapistId));
  };

  const sendReferral = async () => {
    if (!referral.agreed) { setError(t('patients.referAgreeRequired')); return; }
    setBusy(true);
    setError(null);
    // The answer is the same whether or not the patient already sees that
    // professional: that is not the referrer's to learn (0040).
    const { error: referError } = await supabase.rpc('refer_patient', {
      p_dependent_id: referring.id,
      p_therapist_id: referral.therapistId,
      p_patient_agreed: referral.agreed,
      p_note: referral.note.trim() || null,
    });
    setBusy(false);
    if (referError) { setError(referError.message); return; }
    // Worded for both outcomes, because the answer does not say which happened.
    const colleague = directory.find((row) => row.id === referral.therapistId);
    setNotice(t('patients.referSent', {
      name: `${referring.first_name} ${referring.last_name}`,
      professional: colleague ? `${colleague.first_name} ${colleague.last_name}` : t('patients.referProfessional'),
    }));
    setReferring(null);
  };

  const plansForCompany = plans.filter((plan) => plan.company_id === draft.companyId);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('nav.patients')}</h1>
          <p className="text-sm text-slate-500">
            {t('patients.count', { count: currentCount })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/reservar">
            <Button variant="secondary"><CalendarPlus size={16} />{t('booking.title')}</Button>
          </Link>
          <Button onClick={() => setAdding(true)}><UserPlus size={16} />{t('patients.addPatient')}</Button>
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      <Card>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-sm flex-1">
          <Field label={t('common.search')}>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('patients.searchPlaceholder')}
            />
          </Field>
          </div>
          {(dischargedCount > 0 || showDischarged) && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={showDischarged}
                onChange={(event) => setShowDischarged(event.target.checked)}
              />
              {t('patients.showDischarged', { count: dischargedCount })}
            </label>
          )}
        </div>

        {visible.length === 0 ? (
          <EmptyState icon={Users} title={t('patients.none')} />
        ) : (
          <Table headers={[
            t('appointments.patient'), t('insurance.coverage'), t('patients.sessionValue'),
            t('common.status'), t('approvals.accountHolder'), '',
          ]}>
            {visible.map((patient) => {
              const age = ageAt(patient.date_of_birth);
              const active = patient.status === 'approved';
              // none: nothing to invite them with. invited: told, not yet in.
              // active: signing in already. own: an adult under someone else's
              // account, whom that holder no longer sees; they need their own.
              const access = needsOwnAccount(patient)
                ? 'own'
                : !patient.accounts?.email
                  ? 'none'
                  : patient.accounts?.auth_user_id ? 'active' : 'invited';
              // What this patient actually pays. An agreed value overrides
              // whichever default would otherwise apply, so the column shows
              // the effective figure and says where it came from.
              const agreed = patient.fee_override;
              const copay = copays.find((c) => c.insurance_plan_id === patient.insurance_plan_id)?.patient_copay;
              const privateFee = profile?.default_private_fee ?? 0;
              const fee = agreed ?? (patient.billing_mode === 'plan' ? copay : privateFee);
              const row = (
                <tr key={patient.treatment_id}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">
                        {patient.first_name} {patient.last_name}
                      </span>
                      {age != null && <span className="text-xs text-slate-500">{age}</span>}
                    </div>
                    <p className="text-xs text-slate-500">
                      {patient.relationship_to_account === 'self'
                        ? t('patients.self') : patient.relationship_to_account}
                    </p>
                    {patient.consecutive_cancellations >= 3 && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700">
                        <AlertTriangle size={11} />
                        {t('appointments.cancellationWarning', { count: patient.consecutive_cancellations })}
                      </p>
                    )}
                    {active && (
                      <button
                        onClick={() => setExpanded(expanded === patient.id ? null : patient.id)}
                        className="mt-1 flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline"
                      >
                        {expanded === patient.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        {t('patients.upcoming')}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    <Badge tone={patient.billing_mode === 'particular' ? 'neutral' : 'info'}>
                      {t(`insurance.billingModes.${patient.billing_mode}`)}
                    </Badge>
                    {patient.insurance_plans && (
                      <>
                        <p className="mt-1 text-slate-800">{patient.insurance_plans.insurance_companies?.name}</p>
                        <p className="text-xs text-slate-500">{patient.insurance_plans.name}</p>
                      </>
                    )}
                    {patient.needs_invoice && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-slate-600">
                        <Receipt size={11} />{t('patients.needsInvoice')}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {fee == null ? (
                      <span className="text-amber-700">{t('patients.noFeeSet')}</span>
                    ) : (
                      <>
                        {formatMoney(fee)}
                        <p className="text-xs text-slate-400">
                          {agreed != null
                            ? t('patients.agreedValue')
                            : patient.billing_mode === 'plan'
                              ? t('patients.usesCopay')
                              : t('patients.usesDefault')}
                        </p>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <Badge tone={STATUS_TONE[patient.status] ?? 'neutral'}>
                      {patient.status === 'pending'
                        ? t('patients.pendingRequest')
                        : t(`patients.status.${patient.status}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {patient.accounts ? (
                      <>
                        <p className="text-slate-800">
                          {patient.accounts.first_name} {patient.accounts.last_name}
                        </p>
                        <p className="text-xs text-slate-500">{patient.accounts.email}</p>
                        {/* Whether this person can get into their own account,
                            which is otherwise only visible by opening the edit
                            modal one patient at a time — and it is the thing a
                            professional needs to know before telling somebody
                            to go and register. */}
                        <Badge tone={ACCESS_TONE[access]} className="mt-1">
                          {t(`patients.access.${access}`)}
                        </Badge>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {t(`patients.access.${access}Hint`)}
                        </p>
                      </>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      {/* A request is answered where requests are, with who
                          referred them when someone did. */}
                      {patient.status === 'pending' && (
                        <Link to="/solicitudes">
                          <Button variant="secondary">{t('patients.answerRequest')}</Button>
                        </Link>
                      )}
                      {patient.status !== 'pending' && (
                        <Link to={`/historia-clinica?paciente=${patient.id}`}>
                          <Button variant="ghost" title={t('nav.clinicalHistory')}><FileText size={14} /></Button>
                        </Link>
                      )}
                      {active && (
                        <>
                          <Button variant="ghost" onClick={() => openEdit(patient)} title={t('insurance.coverage')}>
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" onClick={() => openReferral(patient)} title={t('patients.refer')}>
                            <Send size={14} />
                          </Button>
                          <Button variant="ghost" onClick={() => discharge(patient)}
                            title={t('patients.discharge')}>
                            <UserMinus size={14} />
                          </Button>
                        </>
                      )}
                      {patient.status === 'revoked' && (
                        <Button variant="ghost" onClick={() => readmit(patient)}
                          title={t('patients.readmit')}>
                          <RotateCcw size={14} />
                        </Button>
                      )}
                      {/* Offered where it can apply, a record whose patient never
                          signed in; the database decides the rest (0062). */}
                      {patient.status !== 'pending' && !patient.accounts?.auth_user_id && (
                        <Button variant="ghost" onClick={() => deleteRecord(patient)}
                          title={t('patients.deleteRecord')}>
                          <Trash2 size={14} className="text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );

              // A row of its own so the panel has the table's full width.
              const detail = expanded === patient.id && active ? (
                <tr key={`${patient.treatment_id}-upcoming`}>
                  <td colSpan={6} className="bg-slate-50 px-3 py-3">
                    <PatientUpcoming patient={patient} onChange={load} />
                  </td>
                </tr>
              ) : null;

              return detail ? [row, detail] : row;
            })}
          </Table>
        )}
      </Card>

      <Modal
        open={adding}
        wide
        title={t('patients.addPatient')}
        onClose={() => setAdding(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
            <Button onClick={createPatient} loading={busy}>{t('common.save')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert tone="info">{t('patients.addExplanation')}</Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('register.firstName')} required>
              <Input value={newPatient.firstName}
                onChange={(e) => setNewPatient({ ...newPatient, firstName: e.target.value })} />
            </Field>
            <Field label={t('register.lastName')} required>
              <Input value={newPatient.lastName}
                onChange={(e) => setNewPatient({ ...newPatient, lastName: e.target.value })} />
            </Field>
            <Field label={t('patients.dateOfBirth')} required hint={t('patients.dobHint')}>
              <Input type="date" value={newPatient.dateOfBirth}
                onChange={(e) => setNewPatient({ ...newPatient, dateOfBirth: e.target.value })} />
            </Field>
            <Field label={t('patients.idNumber')}>
              <Input value={newPatient.idNumber}
                onChange={(e) => setNewPatient({ ...newPatient, idNumber: e.target.value })} />
            </Field>
            <Field label={t('auth.email')} hint={t('patients.emailHint')}>
              <Input type="email" value={newPatient.email}
                onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })} />
            </Field>
            <Field label={t('register.phone')}>
              <Input type="tel" value={newPatient.phone}
                onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })} />
            </Field>
          </div>

          <div className="space-y-4 rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-900">{t('insurance.coverage')}</h3>
            <BillingModeField
              value={newPatient.billingMode}
              onChange={(mode) => setNewPatient({ ...newPatient, billingMode: mode })}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label={newPatient.billingMode === 'particular' ? t('patients.coverageOnFile') : t('insurance.company')}
                required={newPatient.billingMode !== 'particular'}
                hint={newPatient.billingMode === 'particular' ? t('patients.coverageOnFileHint') : undefined}
              >
                <Select value={newPatient.companyId}
                  onChange={(e) => setNewPatient({ ...newPatient, companyId: e.target.value, planId: '' })}>
                  <option value="">
                    {newPatient.billingMode === 'particular' ? t('patients.noCoverageOnFile') : t('register.selectCompany')}
                  </option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
              <Field label={t('insurance.plan')} required={newPatient.billingMode !== 'particular'}>
                <Select value={newPatient.planId} disabled={!newPatient.companyId}
                  onChange={(e) => setNewPatient({ ...newPatient, planId: e.target.value })}>
                  <option value="">{t('register.selectPlan')}</option>
                  {plans.filter((p) => p.company_id === newPatient.companyId)
                    .map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </Field>
              <Field label={t('patients.agreedFee')} hint={agreedFeeHint(newPatient.billingMode)}>
                <Input type="number" min="0" value={newPatient.fee}
                  onChange={(e) => setNewPatient({ ...newPatient, fee: e.target.value })} />
              </Field>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={newPatient.needsInvoice}
                onChange={(e) => setNewPatient({ ...newPatient, needsInvoice: e.target.checked })} />
              <span>
                <span className="font-medium">{t('patients.needsInvoice')}</span>
                <span className="block text-xs text-slate-500">{t('patients.needsInvoiceHint')}</span>
              </span>
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(editing)}
        title={t('patients.editCoverage')}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveCoverage} loading={busy}>{t('common.save')}</Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{editing.first_name} {editing.last_name}</p>

            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">
                {t('patients.contactSection')}
              </h3>
              <Field
                label={t('auth.email')}
                hint={needsOwnAccount(editing)
                  ? t('patients.emailOwnHint')
                  : editing.accounts?.auth_user_id
                    ? t('patients.emailClaimedHint')
                    : t('patients.emailAddHint')}
              >
                <Input
                  type="email"
                  value={draft.email}
                  disabled={Boolean(editing.accounts?.auth_user_id) && !needsOwnAccount(editing)}
                  onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                />
              </Field>
            </div>

            <BillingModeField
              value={draft.billingMode}
              onChange={(mode) => setDraft({ ...draft, billingMode: mode })}
            />

            <div className="grid gap-4 sm:grid-cols-3">
              <Field
                label={draft.billingMode === 'particular' ? t('patients.coverageOnFile') : t('insurance.company')}
                required={draft.billingMode !== 'particular'}
                hint={draft.billingMode === 'particular' ? t('patients.coverageOnFileHint') : undefined}
              >
                <Select value={draft.companyId}
                  onChange={(event) => setDraft({ ...draft, companyId: event.target.value, planId: '' })}>
                  <option value="">
                    {draft.billingMode === 'particular' ? t('patients.noCoverageOnFile') : t('register.selectCompany')}
                  </option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('insurance.plan')} required={draft.billingMode !== 'particular'}>
                <Select value={draft.planId} disabled={!draft.companyId}
                  onChange={(event) => setDraft({ ...draft, planId: event.target.value })}>
                  <option value="">{t('register.selectPlan')}</option>
                  {plansForCompany.map((plan) => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('patients.agreedFee')}
                hint={draft.billingMode === 'particular'
                  ? t('patients.feeHint', { amount: formatMoney(profile?.default_private_fee ?? 0) })
                  : agreedFeeHint(draft.billingMode)}
              >
                <Input type="number" min="0" value={draft.fee}
                  onChange={(event) => setDraft({ ...draft, fee: event.target.value })} />
              </Field>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={draft.needsInvoice}
                onChange={(event) => setDraft({ ...draft, needsInvoice: event.target.checked })} />
              <span>
                <span className="font-medium">{t('patients.needsInvoice')}</span>
                <span className="block text-xs text-slate-500">{t('patients.needsInvoiceHint')}</span>
              </span>
            </label>

            <Alert tone="info">{t('patients.repriceNotice')}</Alert>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(referring)}
        title={referring ? t('patients.referTitle', { name: `${referring.first_name} ${referring.last_name}` }) : ''}
        onClose={() => setReferring(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReferring(null)}>{t('common.cancel')}</Button>
            <Button onClick={sendReferral} loading={busy} disabled={!referral.therapistId || !referral.agreed}>
              <Send size={14} />{t('patients.referSend')}
            </Button>
          </>
        }
      >
        {referring && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{t('patients.referExplanation')}</p>
            <Field label={t('patients.referProfessional')} required>
              <Select value={referral.therapistId}
                onChange={(event) => setReferral({ ...referral, therapistId: event.target.value })}>
                <option value="">{t('patients.referPick')}</option>
                {directory.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.first_name} {row.last_name}{row.discipline ? ` · ${row.discipline}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('patients.referNote')} hint={t('patients.referNoteHint')}>
              <Textarea rows={3} value={referral.note}
                onChange={(event) => setReferral({ ...referral, note: event.target.value })} />
            </Field>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={referral.agreed}
                onChange={(event) => setReferral({ ...referral, agreed: event.target.checked })} />
              <span>
                <span className="font-medium">{t('patients.referAgreed')}</span>
                <span className="block text-xs text-slate-500">{t('patients.referAgreedHint')}</span>
              </span>
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientsView;
